import { isAxiosError } from 'axios'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
	AlertTriangle,
	Bot,
	Building2,
	CheckCircle2,
	ChevronRight,
	FileStack,
	Loader2,
	RefreshCw,
	Users,
	XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useSyncLogs, useSyncProtheus } from '@/api/queries/sync-queries'
import { SyncChangesDialog } from '@/components/administration/sync-changes-dialog'
import { AdminCardPage } from '@/components/commons'
import { Button } from '@/components/ui/button'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type { SyncLogEntry, SyncProtheusResult, SyncType } from '@/types/sync'

// ── Helpers ──────────────────────────────────────────────────────────────────

function SyncTypeIcon({ type }: { type: SyncType }) {
	const cls = 'size-4 shrink-0'
	if (type === 'cr') return <FileStack className={cls} />
	if (type === 'branch') return <Building2 className={cls} />
	return <Users className={cls} />
}

// ── Page ─────────────────────────────────────────────────────────────────────

export const SyncProtheusPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const tp = 'administration.syncProtheus'

	const syncMutation = useSyncProtheus()
	const logsQuery = useSyncLogs()
	const [selectedLog, setSelectedLog] = useState<SyncLogEntry | null>(null)

	// Sync agendado (GitHub Actions) roda em background: bloqueia o botão enquanto houver log RUNNING
	const isRunningInBackground =
		logsQuery.data?.items.some((log) => log.status === 'RUNNING') ?? false
	const isSyncing = syncMutation.isPending || isRunningInBackground

	const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000
	const lastSyncAt = logsQuery.data?.items[0]?.createdAt
	const isRecentlySynced =
		!!lastSyncAt &&
		Date.now() - new Date(lastSyncAt).getTime() < TWELVE_HOURS_MS
	const nextSyncAt = lastSyncAt
		? new Date(new Date(lastSyncAt).getTime() + TWELVE_HOURS_MS)
		: null

	const handleSync = async () => {
		try {
			const result = await syncMutation.mutateAsync()
			const totalCreated =
				result.branch.created + result.cr.created + result.person.created
			const totalUpdated =
				result.branch.updated + result.cr.updated + result.person.updated
			const totalRemoved =
				result.branch.removed + result.cr.removed + result.person.removed
			toast.success(
				`${t(`${tp}.syncSuccess`)} — ${totalCreated} ${t(`${tp}.created`)}, ${totalUpdated} ${t(`${tp}.updated`)}, ${totalRemoved} ${t(`${tp}.removed`)}.`,
			)
		} catch (error) {
			toast.error(
				isAxiosError(error) && error.response?.status === 409
					? t(`${tp}.syncAlreadyRunning`)
					: t(`${tp}.syncError`),
			)
		}
	}

	return (
		<AdminCardPage
			icon={<RefreshCw className="size-5" />}
			title={t(`${tp}.title`)}
			subtitle={t(`${tp}.subtitle`)}
			onBack={() =>
				navigate(adminBackRoute(ROUTES.ADMINISTRATION.SYNC_PROTHEUS))
			}
		>
			{/* ── Sync card ───────────────────────────────────────────────── */}
			<div className="flex flex-col gap-4 rounded-xl border bg-muted/30 px-5 py-5">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex flex-col gap-2">
						<div>
							<h3 className="text-sm font-semibold text-kaefer-dark-grey">
								{t(`${tp}.syncSectionTitle`)}
							</h3>
							<p className="mt-0.5 text-xs text-muted-foreground">
								{t(`${tp}.syncSectionSubtitle`)}
							</p>
						</div>
						<div className="flex flex-wrap gap-2">
							{(
								[
									{
										type: 'branch',
										icon: <Building2 className="size-3" />,
										label: t(`${tp}.typeBranch`),
									},
									{
										type: 'cr',
										icon: <FileStack className="size-3" />,
										label: t(`${tp}.typeCr`),
									},
									{
										type: 'person',
										icon: <Users className="size-3" />,
										label: t(`${tp}.typePerson`),
									},
								] as const
							).map(({ type, icon, label }) => (
								<span
									key={type}
									className="inline-flex items-center gap-1 rounded-full border bg-background px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
								>
									{icon}
									{label}
								</span>
							))}
						</div>
					</div>

					{/* Start button */}
					<div className="flex flex-col items-end gap-1 sm:shrink-0">
						<Button
							className="gap-2 bg-kaefer-medium-grey hover:bg-kaefer-medium-grey/90 w-full"
							disabled={isSyncing /*  || isRecentlySynced */}
							onClick={handleSync}
						>
							{isSyncing ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									{t(`${tp}.syncing`)}
								</>
							) : (
								<>
									<RefreshCw className="size-4" />
									{t(`${tp}.startSync`)}
								</>
							)}
						</Button>
						{isRecentlySynced && nextSyncAt && (
							<p className="text-xs text-muted-foreground">
								{t(`${tp}.nextSyncAvailable`)}{' '}
								<span className="font-medium">
									{format(nextSyncAt, 'HH:mm', { locale: ptBR })}
								</span>
							</p>
						)}
					</div>
				</div>

				{/* Inline result after sync */}
				{syncMutation.isSuccess && syncMutation.data && (
					<SyncResultBanner result={syncMutation.data} t={t} tp={tp} />
				)}

				{syncMutation.isError && (
					<div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
						<AlertTriangle className="size-4 shrink-0" />
						{t(`${tp}.syncError`)}
					</div>
				)}
			</div>

			{/* ── Log table ───────────────────────────────────────────────── */}
			<div className="flex flex-col gap-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-semibold text-kaefer-dark-grey">
						{t(`${tp}.logsTitle`)}
					</h3>
					<Button
						variant="ghost"
						size="sm"
						className="gap-1.5 text-xs text-muted-foreground"
						disabled={logsQuery.isFetching}
						onClick={() => logsQuery.refetch()}
					>
						<RefreshCw
							className={`size-3 ${logsQuery.isFetching ? 'animate-spin' : ''}`}
						/>
						{t(`${tp}.refresh`)}
					</Button>
				</div>

				{logsQuery.isLoading ? (
					<div className="flex items-center justify-center py-10 text-muted-foreground">
						<Loader2 className="mr-2 size-5 animate-spin" />
						{t(`${tp}.loadingLogs`)}
					</div>
				) : logsQuery.isError ? (
					<div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
						<AlertTriangle className="size-4 shrink-0" />
						{t(`${tp}.logsError`)}
					</div>
				) : (logsQuery.data?.items.length ?? 0) === 0 ? (
					<p className="py-8 text-center text-sm text-muted-foreground">
						{t(`${tp}.noLogs`)}
					</p>
				) : (
					<SyncLogTable
						entries={logsQuery.data?.items ?? []}
						t={t}
						tp={tp}
						onSelect={setSelectedLog}
					/>
				)}
			</div>

			<SyncChangesDialog
				log={selectedLog}
				onClose={() => setSelectedLog(null)}
			/>
		</AdminCardPage>
	)
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SyncResultBanner({
	result,
	t,
	tp,
}: {
	result: SyncProtheusResult
	t: (k: string) => string
	tp: string
}) {
	const rows: { type: SyncType; icon: React.ReactNode; label: string }[] = [
		{
			type: 'branch',
			icon: <Building2 className="size-3.5" />,
			label: t(`${tp}.typeBranch`),
		},
		{
			type: 'cr',
			icon: <FileStack className="size-3.5" />,
			label: t(`${tp}.typeCr`),
		},
		{
			type: 'person',
			icon: <Users className="size-3.5" />,
			label: t(`${tp}.typePerson`),
		},
	]

	return (
		<div className="overflow-hidden rounded-lg border bg-green-50/60">
			<Table>
				<TableHeader>
					<TableRow className="border-b">
						<TableHead className="px-3 py-1.5 text-xs font-semibold text-muted-foreground" />
						<TableHead className="px-3 py-1.5 text-center text-xs font-semibold text-green-700">
							{t(`${tp}.created`)}
						</TableHead>
						<TableHead className="px-3 py-1.5 text-center text-xs font-semibold text-blue-700">
							{t(`${tp}.updated`)}
						</TableHead>
						<TableHead className="px-3 py-1.5 text-center text-xs font-semibold text-amber-700">
							{t(`${tp}.removed`)}
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map(({ type, icon, label }) => (
						<TableRow key={type}>
							<TableCell className="px-3 py-2">
								<span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
									{icon}
									{label}
								</span>
							</TableCell>
							<TableCell className="px-3 py-2 text-center font-semibold text-green-700">
								{result[type].created}
							</TableCell>
							<TableCell className="px-3 py-2 text-center font-semibold text-blue-700">
								{result[type].updated}
							</TableCell>
							<TableCell className="px-3 py-2 text-center font-semibold text-amber-700">
								{result[type].removed}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}

function SyncStatusIcon({
	entry,
	t,
	tp,
}: {
	entry: SyncLogEntry
	t: (k: string) => string
	tp: string
}) {
	if (entry.status === 'RUNNING') {
		return (
			<Loader2
				className="size-4 animate-spin text-blue-600"
				aria-label={t(`${tp}.status_RUNNING`)}
			/>
		)
	}
	if (entry.status === 'FAILED') {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<XCircle
						className="size-4 text-red-600"
						aria-label={t(`${tp}.status_FAILED`)}
					/>
				</TooltipTrigger>
				<TooltipContent className="max-w-xs break-all">
					{entry.error ?? t(`${tp}.status_FAILED`)}
				</TooltipContent>
			</Tooltip>
		)
	}
	return (
		<CheckCircle2
			className="size-4 text-green-600"
			aria-label={t(`${tp}.status_SUCCESS`)}
		/>
	)
}

function SyncLogTable({
	entries,
	t,
	tp,
	onSelect,
}: {
	entries: SyncLogEntry[]
	t: (k: string) => string
	tp: string
	onSelect: (entry: SyncLogEntry) => void
}) {
	const formatDate = (iso: string) => {
		try {
			return format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: ptBR })
		} catch {
			return iso
		}
	}

	return (
		<div className="rounded-xl border">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						{[
							t(`${tp}.logColType`),
							t(`${tp}.logColDate`),
							t(`${tp}.logColUser`),
							t(`${tp}.logColCreated`),
							t(`${tp}.logColUpdated`),
							t(`${tp}.logColRemoved`),
							'',
						].map((h) => (
							<TableHead
								key={h}
								className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
							>
								{h}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{entries.map((entry) => (
						<TableRow
							key={entry.id}
							className="cursor-pointer hover:bg-muted/40"
							onClick={() => onSelect(entry)}
						>
							<TableCell className="px-3 py-2">
								<span className="flex items-center gap-1.5">
									<SyncStatusIcon entry={entry} t={t} tp={tp} />
									<SyncTypeIcon type={entry.syncType} />
									<span className="font-medium capitalize">
										{t(`${tp}.type_${entry.syncType}`)}
									</span>
								</span>
							</TableCell>
							<TableCell className="px-3 py-2 text-xs text-muted-foreground">
								{formatDate(entry.createdAt)}
							</TableCell>
							<TableCell className="max-w-36 truncate px-3 py-2 text-xs">
								{entry.user ? (
									entry.user.name
								) : (
									<span className="inline-flex items-center gap-1 text-muted-foreground">
										<Bot className="size-3.5" />
										{t(`${tp}.system`)}
									</span>
								)}
							</TableCell>
							<TableCell className="px-3 py-2 text-center font-medium text-green-700">
								{entry.created}
							</TableCell>
							<TableCell className="px-3 py-2 text-center font-medium text-blue-700">
								{entry.updated}
							</TableCell>
							<TableCell className="px-3 py-2 text-center font-medium text-amber-700">
								{entry.removed}
							</TableCell>
							<TableCell className="px-3 py-2 text-right">
								<ChevronRight
									className="ml-auto size-4 text-muted-foreground"
									aria-label={t(`${tp}.viewChanges`)}
								/>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}
